import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const SharedPieChart = React.lazy(() => import("../../Sharedpiechart"));

const TITLE = "Calculatrice de marge beneficiaire - Marge brute, operationnelle et nette";
const DESCRIPTION =
  "Calculatrice de marge beneficiaire gratuite : calculez la marge brute, la marge operationnelle et la marge nette de votre activite. Outil ideal pour commercants, entrepreneurs et comptables.";
const KEYWORDS =
  "calculatrice marge beneficiaire, marge brute, marge operationnelle, marge nette, calcul rentabilite";
const OG_URL = "https://do-calculate.com/calculator/fr/profit-margin-calculator/";

function ProfitMarginCalculator() {
  const [calculatorType, setCalculatorType] = useState("gross");

  const [grossRevenue, setGrossRevenue] = useState("");
  const [cogs, setCogs] = useState("");
  const [grossProfit, setGrossProfit] = useState("");
  const [grossMargin, setGrossMargin] = useState("");

  const [operatingRevenue, setOperatingRevenue] = useState("");
  const [operatingCogs, setOperatingCogs] = useState("");
  const [operatingExpenses, setOperatingExpenses] = useState("");
  const [operatingProfit, setOperatingProfit] = useState("");
  const [operatingMargin, setOperatingMargin] = useState("");

  const [netRevenue, setNetRevenue] = useState("");
  const [netProfit, setNetProfit] = useState("");
  const [netMargin, setNetMargin] = useState("");

  const [error, setError] = useState("");
  const [showChart, setShowChart] = useState(false);

  const handleGrossProfitCalculate = () => {
    if (!grossRevenue || !cogs) {
      setError("Veuillez entrer le chiffre d&apos;affaires total et le cout des marchandises vendues");
      setGrossProfit(""); setGrossMargin(""); setShowChart(false); return;
    }
    const revenue = parseFloat(grossRevenue);
    const costOfGoods = parseFloat(cogs);
    if (isNaN(revenue) || isNaN(costOfGoods) || revenue <= 0 || costOfGoods < 0) {
      setError("Veuillez entrer des valeurs valides");
      setGrossProfit(""); setGrossMargin(""); setShowChart(false); return;
    }
    if (costOfGoods > revenue) {
      setError("Le cout des marchandises ne peut pas depasser le chiffre d&apos;affaires");
      setGrossProfit(""); setGrossMargin(""); setShowChart(false); return;
    }
    const profit = revenue - costOfGoods;
    const margin = (profit / revenue) * 100;
    setGrossProfit(profit.toFixed(2));
    setGrossMargin(margin.toFixed(2));
    setError(""); setShowChart(true);
  };

  const handleOperatingProfitCalculate = () => {
    if (!operatingRevenue || !operatingCogs || !operatingExpenses) {
      setError("Veuillez remplir tous les champs requis");
      setOperatingProfit(""); setOperatingMargin(""); setShowChart(false); return;
    }
    const revenue = parseFloat(operatingRevenue);
    const costOfGoods = parseFloat(operatingCogs);
    const expenses = parseFloat(operatingExpenses);
    if (isNaN(revenue) || isNaN(costOfGoods) || isNaN(expenses) || revenue <= 0 || costOfGoods < 0 || expenses < 0) {
      setError("Veuillez entrer des valeurs valides");
      setOperatingProfit(""); setOperatingMargin(""); setShowChart(false); return;
    }
    const grossProfit = revenue - costOfGoods;
    const opProfit = grossProfit - expenses;
    const opMargin = (opProfit / revenue) * 100;
    setOperatingProfit(opProfit.toFixed(2));
    setOperatingMargin(opMargin.toFixed(2));
    setError(""); setShowChart(true);
  };

  const handleNetProfitCalculate = () => {
    if (!netRevenue || !netProfit) {
      setError("Veuillez entrer le chiffre d&apos;affaires net et le benefice net");
      setNetMargin(""); setShowChart(false); return;
    }
    const revenue = parseFloat(netRevenue);
    const profit = parseFloat(netProfit);
    if (isNaN(revenue) || isNaN(profit) || revenue <= 0) {
      setError("Veuillez entrer des valeurs valides");
      setNetMargin(""); setShowChart(false); return;
    }
    const margin = (profit / revenue) * 100;
    setNetMargin(margin.toFixed(2));
    setError(""); setShowChart(true);
  };

  const handleReset = () => {
    setGrossRevenue(""); setCogs(""); setGrossProfit(""); setGrossMargin("");
    setOperatingRevenue(""); setOperatingCogs(""); setOperatingExpenses("");
    setOperatingProfit(""); setOperatingMargin("");
    setNetRevenue(""); setNetProfit(""); setNetMargin("");
    setError(""); setShowChart(false);
  };

  const renderCalculator = () => {
    switch (calculatorType) {
      case "gross":
        return (
          <div>
            <div className="form-group mb-3">
              <label><b>Chiffre d&apos;affaires total (ventes nettes) :</b></label>
              <input type="number" placeholder="Entrez le chiffre d&apos;affaires" value={grossRevenue}
                onChange={(e) => setGrossRevenue(e.target.value)} className="form-control" />
            </div>
            <div className="form-group mb-3">
              <label><b>Cout des marchandises vendues (CMV) :</b></label>
              <input type="number" placeholder="Entrez le cout des marchandises" value={cogs}
                onChange={(e) => setCogs(e.target.value)} className="form-control" />
            </div>
            <div className="d-flex gap-2 mb-3">
              <button className="btn btn-success" onClick={handleGrossProfitCalculate}>
                Calculer la marge brute
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>Reinitialiser</button>
            </div>
            {error && <p className="p-3 mb-2 bg-warning text-white">Erreur : {error}</p>}
            {!error && grossProfit && (
              <>
                <div className="result-box p-3 mb-3 bg-light border">
                  <h3 style={{ fontSize: "18px", color: "#28a745" }}>
                    <b>Benefice brut : {grossProfit}</b>
                  </h3>
                  <p style={{ fontSize: "14px", color: "#666" }}>Benefice = Chiffre d&apos;affaires - Cout des marchandises</p>
                </div>
                <div className="result-box p-3 mb-3 bg-success text-white">
                  <h3 style={{ fontSize: "20px" }}>
                    <b>Marge brute : {grossMargin}%</b>
                  </h3>
                  <p style={{ fontSize: "14px" }}>
                    Pour 100 de ventes, vous generez {grossMargin} de benefice brut
                  </p>
                </div>
              </>
            )}
          </div>
        );
      case "operating":
        return (
          <div>
            <div className="form-group mb-3">
              <label><b>Chiffre d&apos;affaires total :</b></label>
              <input type="number" placeholder="Entrez le chiffre d&apos;affaires" value={operatingRevenue}
                onChange={(e) => setOperatingRevenue(e.target.value)} className="form-control" />
            </div>
            <div className="form-group mb-3">
              <label><b>Cout des marchandises vendues :</b></label>
              <input type="number" placeholder="Entrez le cout des marchandises" value={operatingCogs}
                onChange={(e) => setOperatingCogs(e.target.value)} className="form-control" />
            </div>
            <div className="form-group mb-3">
              <label><b>Charges d&apos;exploitation (admin, marketing) :</b></label>
              <input type="number" placeholder="Entrez les charges d&apos;exploitation" value={operatingExpenses}
                onChange={(e) => setOperatingExpenses(e.target.value)} className="form-control" />
            </div>
            <div className="d-flex gap-2 mb-3">
              <button className="btn btn-success" onClick={handleOperatingProfitCalculate}>
                Calculer la marge operationnelle
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>Reinitialiser</button>
            </div>
            {error && <p className="p-3 mb-2 bg-warning text-white">Erreur : {error}</p>}
            {!error && operatingProfit && (
              <>
                <div className="result-box p-3 mb-3 bg-light border">
                  <h3 style={{ fontSize: "18px", color: "#28a745" }}>
                    <b>Resultat d&apos;exploitation : {operatingProfit}</b>
                  </h3>
                  <p style={{ fontSize: "14px", color: "#666" }}>
                    Resultat operationnel = CA - Cout marchandises - Charges exploitation
                  </p>
                </div>
                <div className="result-box p-3 mb-3 bg-success text-white">
                  <h3 style={{ fontSize: "20px" }}>
                    <b>Marge operationnelle : {operatingMargin}%</b>
                  </h3>
                  <p style={{ fontSize: "14px" }}>Efficacite des operations commerciales</p>
                </div>
              </>
            )}
          </div>
        );
      case "net":
        return (
          <div>
            <div className="form-group mb-3">
              <label><b>Chiffre d&apos;affaires net (ventes totales) :</b></label>
              <input type="number" placeholder="Entrez le chiffre d&apos;affaires net" value={netRevenue}
                onChange={(e) => setNetRevenue(e.target.value)} className="form-control" />
            </div>
            <div className="form-group mb-3">
              <label><b>Benefice net (apres toutes les charges) :</b></label>
              <input type="number" placeholder="Entrez le benefice net" value={netProfit}
                onChange={(e) => setNetProfit(e.target.value)} className="form-control" />
            </div>
            <div className="d-flex gap-2 mb-3">
              <button className="btn btn-success" onClick={handleNetProfitCalculate}>
                Calculer la marge nette
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>Reinitialiser</button>
            </div>
            {error && <p className="p-3 mb-2 bg-warning text-white">Erreur : {error}</p>}
            {!error && netMargin && (
              <>
                <div className="result-box p-3 mb-3 bg-success text-white">
                  <h3 style={{ fontSize: "20px" }}>
                    <b>Marge nette : {netMargin}%</b>
                  </h3>
                  <p style={{ fontSize: "14px" }}>
                    Pour 100 de ventes, il reste {netMargin} de benefice net
                  </p>
                </div>
                <div className="result-box p-3 mb-3 bg-info text-black">
                  <p style={{ fontSize: "14px", margin: 0 }}>
                    <b>Interpretation :</b> {parseFloat(netMargin) >= 15 ? "Rentabilite excellente" : parseFloat(netMargin) >= 10 ? "Bonne rentabilite" : parseFloat(netMargin) >= 5 ? "Rentabilite moyenne" : "Rentabilite faible"}
                  </p>
                </div>
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="ltr">
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} />
      <div className="row"><div className="col-12 col-md-8"><FirstAds /></div></div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ height: "50px" }}>
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <p>
            Calculez vos revenus et benefices facilement avec cet outil gratuit. Entrez le cout du produit
            ou service et le taux de marge souhaite, et la calculatrice genere les resultats instantanement.
            Ideale pour les petites et grandes entreprises, commercants et etudiants en gestion.
          </p>
          <ul>
            <li><b>Chiffre d&apos;affaires = Cout du produit / (1 - (Marge / 100))</b></li>
            <li><b>Benefice = Chiffre d&apos;affaires - Cout du produit</b></li>
          </ul>
          <MobileUnderCalAds />
          <div className="border border-light">
            <div className="row">
              <div className="col-sm-12 col-md-12 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>Calculatrice de marge beneficiaire</h2>
                </div>
                <hr />
                <div className="mb-3">
                  <label><b>Choisissez le type de calcul de marge :</b></label>
                  <select className="form-select" value={calculatorType} onChange={(e) => { setCalculatorType(e.target.value); handleReset(); }}>
                    <option value="gross">Marge brute (Gross Profit Margin)</option>
                    <option value="operating">Marge operationnelle (Operating Profit Margin)</option>
                    <option value="net">Marge nette (Net Profit Margin)</option>
                  </select>
                </div>
                <hr />
                {renderCalculator()}
              </div>
            </div>
            {showChart && calculatorType === "gross" && grossMargin && (
              <div className="row">
                <div className="col-sm-12 col-md-4">
                  <React.Suspense fallback={<div className="text-center p-3">Chargement...</div>}>
                    <SharedPieChart
                      firstLabel="Marge beneficiaire"
                      secondLabel="Part des couts"
                      firstValue={grossMargin}
                      secondValue={(100 - parseFloat(grossMargin)).toFixed(2)}
                      firstColor="#28a745"
                      secondColor="#dc3545"
                      title="Repartition benefice / cout"
                    />
                  </React.Suspense>
                </div>
              </div>
            )}
            <hr />
            <div className="row"><RelatedLinks /><hr /></div>
          </div>
        </div>
        <div className="col">
          <SecondAds /><br />
          <MainCalculatorsList />
          <br />
        </div>
      </div>
      <div className="row" style={{ height: "auto" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">Qu&apos;est-ce qu&apos;une calculatrice de marge beneficiaire ?</div>
              <div className="info-section-body">
                <h2 style={{ fontSize: "18px" }}><u>Qu&apos;est-ce qu&apos;une calculatrice de marge beneficiaire ?</u></h2>
                <p>
                  <b>La calculatrice de marge beneficiaire</b> est un outil financier gratuit qui vous aide a calculer
                  les differentes marges de rentabilite de votre activite. Elle permet de calculer trois types principaux :
                </p>
                <ul>
                  <li><b>Marge brute (Gross Profit Margin) :</b> mesure l&apos;efficacite dans la gestion des couts directs de production</li>
                  <li><b>Marge operationnelle (Operating Profit Margin) :</b> reflete l&apos;efficacite des operations commerciales quotidiennes</li>
                  <li><b>Marge nette (Net Profit Margin) :</b> affiche la rentabilite reelle apres deduction de toutes les charges</li>
                </ul>
                <p>
                  Que vous soyez proprietaire d&apos;une petite entreprise, responsable financier, comptable ou investisseur,
                  cet outil vous offre une vue complete sur la performance financiere de votre activite.
                </p>
                <hr />
                <h3 style={{ fontSize: "18px" }}><u>Comment utiliser la calculatrice de marge beneficiaire :</u></h3>
                <ol>
                  <li><b>Choisissez le type de marge :</b> selectionnez brute, operationnelle ou nette</li>
                  <li><b>Entrez les donnees financieres :</b> renseignez les montants dans les champs prevus</li>
                  <li><b>Calculez le resultat :</b> cliquez sur "Calculer" pour obtenir le pourcentage</li>
                  <li><b>Analysez les resultats :</b> interpretez les chiffres pour comprendre votre performance</li>
                  <li><b>Reinitialiser :</b> utilisez "Reinitialiser" pour demarrer un nouveau calcul</li>
                </ol>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Comment calculer la marge brute</div>
              <div className="info-section-body">
                <h2 style={{ fontSize: "18px" }}><u>Marge brute (Gross Profit Margin)</u></h2>
                <p><b>La marge brute</b> mesure l&apos;efficacite de l&apos;entreprise dans la production ou l&apos;achat de marchandises par rapport au chiffre d&apos;affaires. Elle represente le benefice restant apres deduction des couts directs de production.</p>
                <div className="p-3 mb-2 bg-info text-black">
                  <p><b>Formule :</b></p>
                  <p>Benefice brut = Chiffre d&apos;affaires net - Cout des marchandises vendues</p>
                  <p>Marge brute = (Benefice brut ÷ Chiffre d&apos;affaires net) × 100</p>
                </div>
                <p><b>Exemple pratique :</b></p>
                <ul>
                  <li>Chiffre d&apos;affaires net = 100 000 €</li>
                  <li>Cout des marchandises vendues = 60 000 €</li>
                  <li>Benefice brut = 100 000 - 60 000 = 40 000 €</li>
                  <li>Marge brute = (40 000 ÷ 100 000) × 100 = <b>40 %</b></li>
                </ul>
                <p><b>Interpretation :</b> Pour chaque 100 € de ventes, l&apos;entreprise realise 40 € de benefice brut avant de payer les charges operationnelles.</p>
                <p><b>Le cout des marchandises vendues comprend :</b></p>
                <ul>
                  <li>Cout des matieres premieres</li>
                  <li>Main-d&apos;oeuvre directe de production</li>
                  <li>Couts de fabrication directs</li>
                  <li>Frais de transport et d&apos;importation</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Comment calculer la marge operationnelle</div>
              <div className="info-section-body">
                <h2 style={{ fontSize: "18px" }}><u>Marge operationnelle (Operating Profit Margin)</u></h2>
                <p><b>La marge operationnelle</b> reflete le benefice issu des activites principales de l&apos;entreprise, apres deduction de toutes les charges operationnelles (salaires administratifs, marketing, loyer).</p>
                <div className="p-3 mb-2 bg-info text-black">
                  <p><b>Formule :</b></p>
                  <p>Benefice operationnel = CA net - Cout des marchandises - Charges operationnelles</p>
                  <p>Marge operationnelle = (Benefice operationnel ÷ CA net) × 100</p>
                </div>
                <p><b>Exemple pratique :</b></p>
                <ul>
                  <li>Chiffre d&apos;affaires net = 100 000 €</li>
                  <li>Cout des marchandises = 60 000 €</li>
                  <li>Charges operationnelles = 20 000 €</li>
                  <li>Benefice operationnel = 40 000 - 20 000 = 20 000 €</li>
                  <li>Marge operationnelle = (20 000 ÷ 100 000) × 100 = <b>20 %</b></li>
                </ul>
                <p><b>Les charges operationnelles comprennent :</b></p>
                <ul>
                  <li>Salaires du personnel administratif</li>
                  <li>Frais de marketing et publicite</li>
                  <li>Loyers et charges d&apos;exploitation</li>
                  <li>Frais de recherche et developpement</li>
                  <li>Frais de maintenance</li>
                  <li>Amortissements</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Comment calculer la marge nette</div>
              <div className="info-section-body">
                <h2 style={{ fontSize: "18px" }}><u>Marge nette (Net Profit Margin)</u></h2>
                <p><b>La marge nette</b> est l&apos;indicateur le plus complet : elle exprime le pourcentage du chiffre d&apos;affaires qui reste en benefice net apres deduction de TOUTES les charges sans exception.</p>
                <div className="p-3 mb-2 bg-info text-black">
                  <p><b>Formule :</b></p>
                  <p>Benefice net = CA - Cout des marchandises - Charges operationnelles - Impots - Autres charges</p>
                  <p>Marge nette = (Benefice net ÷ CA net) × 100</p>
                </div>
                <p><b>Exemple complet :</b></p>
                <ul>
                  <li>Chiffre d&apos;affaires = 100 000 €</li>
                  <li>Cout des marchandises = 60 000 € → Benefice brut = 40 000 € (40 %)</li>
                  <li>Charges operationnelles = 20 000 € → Benefice operationnel = 20 000 € (20 %)</li>
                  <li>Impots et taxes = 5 000 €</li>
                  <li>Benefice net = 15 000 €</li>
                  <li>Marge nette = (15 000 ÷ 100 000) × 100 = <b>15 %</b></li>
                </ul>
                <p><b>Benchmarks de marge nette :</b></p>
                <ul>
                  <li><b>Excellent :</b> 15 % et plus — entreprise tres rentable</li>
                  <li><b>Bon :</b> 10-15 % — bonne performance financiere</li>
                  <li><b>Moyen :</b> 5-10 % — rentabilite correcte</li>
                  <li><b>Faible :</b> moins de 5 % — necessite une amelioration</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Utilisations de la calculatrice de marge beneficiaire</div>
              <div className="info-section-body">
                <ol>
                  <li><b>Determiner la rentabilite des produits :</b> identifiez quels produits sont les plus profitables.</li>
                  <li><b>Decisions de tarification :</b> fixez le bon prix en fonction de la marge nette cible.</li>
                  <li><b>Analyse de la performance operationnelle :</b> evaluez l&apos;efficacite des charges quotidiennes.</li>
                  <li><b>Evaluer la rentabilite finale :</b> comprenez le profit reel apres toutes les deductions.</li>
                  <li><b>Comparer avec les concurrents :</b> positionnez-vous par rapport aux moyennes du secteur.</li>
                  <li><b>Analyse de faisabilite :</b> testez differents scenarios avant de lancer un produit.</li>
                  <li><b>Preparation des rapports financiers :</b> fournissez des donnees precises pour les bilans.</li>
                  <li><b>Attirer des investisseurs :</b> presentez des marges solides pour prouver la viabilite.</li>
                  <li><b>Planification strategique :</b> fixez des objectifs financiers realistes et mesurez les progres.</li>
                </ol>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Questions frequentes sur la marge beneficiaire</div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "16px" }}><b>Quelle est la difference entre marge et taux de marge ?</b></h3>
                <p>La <b>marge beneficiaire</b> (calculee sur le prix de vente) et le <b>taux de marque</b> sont deux concepts distincts. La marge = (PV - PC) / PV × 100. Le taux de marque = (PV - PC) / PC × 100. Exemple : un produit achete 100 € et vendu 150 € a une marge de 33,3 % mais un taux de marque de 50 %.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><b>Comment calculer le prix de vente a partir d&apos;une marge cible ?</b></h3>
                <p>Prix de vente = Cout d&apos;achat ÷ (1 - Marge cible). Exemple : cout 100 €, marge cible 30 % → PV = 100 ÷ 0,70 = 142,86 €.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><b>Quelle est une bonne marge beneficiaire selon le secteur ?</b></h3>
                <ul>
                  <li><b>Commerce de detail :</b> 2-5 % de marge nette</li>
                  <li><b>Commerce de gros :</b> 5-15 %</li>
                  <li><b>Restauration :</b> 3-8 %</li>
                  <li><b>Logiciels / Tech :</b> 15-25 %</li>
                  <li><b>Conseil :</b> 10-20 %</li>
                  <li><b>Industrie manufacturiere :</b> 5-10 %</li>
                </ul>
                <hr />
                <h3 style={{ fontSize: "16px" }}><b>Pourquoi la marge nette est-elle plus importante que la marge brute ?</b></h3>
                <p>La marge nette donne une image complete de la rentabilite reelle car elle tient compte de toutes les charges y compris les impots. Une entreprise peut afficher une marge brute elevee mais, si ses charges operationnelles sont excessives, se retrouver avec une marge nette tres faible voire negative.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><b>Comment ameliorer la marge beneficiaire de mon entreprise ?</b></h3>
                <ol>
                  <li><b>Augmenter les prix :</b> si la valeur offerte le justifie</li>
                  <li><b>Reduire le cout des marchandises :</b> negocier avec les fournisseurs, acheter en gros</li>
                  <li><b>Diminuer les charges operationnelles :</b> automatiser, optimiser les processus</li>
                  <li><b>Augmenter le volume de ventes :</b> marketing efficace, elargir la clientele</li>
                  <li><b>Privilegier les produits les plus rentables :</b> analyser la marge par produit</li>
                  <li><b>Ameliorer la gestion des stocks :</b> reduire les pertes et les invendus</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfitMarginCalculator;
